import { DatePipe } from '@angular/common';
import { Component, computed, inject, input, output, signal } from '@angular/core';
import { DateAdapter, provideNativeDateAdapter } from '@angular/material/core';
import {
    MatDatepickerInputEvent,
    MatDatepickerModule,
} from '@angular/material/datepicker';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaButtonComponent } from 'src/app/shared/components/ra-button/ra-button.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';

/**
 * Modal para definir a data de uma imagem (única ou foto de grupo) — paridade
 * com o app, onde a data é editada pelo RaImageDateSheet.
 *
 * O calendário é o Datepicker do Angular Material (dependência que o projeto
 * já carrega), no idioma ativo; o input visível continua com o estilo do DS.
 * Emite a data como meia-noite LOCAL (o chamador manda `toISOString()`, o
 * mesmo instante UTC da meia-noite local que o app envia, evitando o
 * off-by-one na exibição).
 */
@Component({
    selector: 'modal-image-date',
    templateUrl: 'modal-image-date.component.html',
    styleUrl: 'modal-image-date.component.scss',
    providers: [provideNativeDateAdapter()],
    imports: [
        DatePipe,
        MatDatepickerModule,
        RaButtonComponent,
        RaModalComponent,
        TranslatePipe,
    ],
})
export class ModalImageDateComponent {
    /** Data atual da imagem (preenche o calendário ao abrir). */
    public date = input<Date | undefined>(undefined);

    public save = output<Date>();
    public close = output<void>();

    /** Data escolhida no calendário; null = ainda usa a inicial. */
    private _picked = signal<Date | null>(null);

    public selectedDate = computed<Date | null>(() => {
        const picked = this._picked();
        if (picked) return picked;

        const initial = this.date();
        if (!initial) return null;
        const parsed = new Date(initial);
        return isNaN(parsed.getTime()) ? null : parsed;
    });

    constructor() {
        // Calendário no idioma ativo (nomes de mês/dias via Intl).
        const adapter = inject<DateAdapter<Date>>(DateAdapter);
        const translate = inject(TranslateService);
        adapter.setLocale(translate.currentLang || translate.defaultLang || 'pt-BR');
    }

    public onDateChange(event: MatDatepickerInputEvent<Date>): void {
        if (event.value) this._picked.set(event.value);
    }

    public onConfirm(): void {
        const selected = this.selectedDate();
        if (!selected) return;

        // Meia-noite local do dia escolhido (o datepicker já entrega assim,
        // normalizamos por segurança).
        this.save.emit(
            new Date(selected.getFullYear(), selected.getMonth(), selected.getDate())
        );
    }
}
